import { ArrowRightOutlined } from '@ant-design/icons'
import { Button, Checkbox, Dropdown, Empty } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import dayjs from 'dayjs'
import { FC, useState } from 'react'
import styled from 'styled-components'
import connect from '../../../../../../../helpers/api/connect'
import useErrorModal from '../../../../../../../hooks/api/use-error-modal'
import useSuccessModal from '../../../../../../../hooks/api/use-success-modal'
import { userStorage } from '../../../../../../../services/user-storage'
import { rewindJobsApi, useUpdateRewindJobMutation } from '../../../../../../../store/api/rewind-jobs-api'
import {
    useCreateRewindQueueEntryMutation,
    useLazyGetRewindQueueEntriesQuery,
} from '../../../../../../../store/api/rewind-queue-entries-api'
import { useAppDispatch } from '../../../../../../../store/store'
import Machine from '../../../../../../../types/api/machine'
import RewindJob, { RewindJobStatuses } from '../../../../../../../types/api/rewind-job'
import { Zones } from '../../../../../../../types/api/rewind-queue-entry'

const DropdownContent = styled.div`
    min-width: 180px;
    padding: 8px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colorBgElevated};
    box-shadow: ${({ theme }) => theme.boxShadowSecondary};
`

const MachineCheckbox = styled.div`
    margin-bottom: 6px;
`

interface AddToMachineQueueProps {
    rewindJob: RewindJob
    machines: Machine[]
}

const AddToMachineQueue: FC<AddToMachineQueueProps> = ({ rewindJob, machines }) => {
    const dispatch = useAppDispatch()
    const [selected, setSelected] = useState<Machine[]>([])
    const [getRewindQueueEntries, getRewindQueueEntriesState] = useLazyGetRewindQueueEntriesQuery()
    const [createRewindQueueEntry, createRewindQueueEntryState] = useCreateRewindQueueEntryMutation()
    const [updateRewindJob, updateRewindJobState] = useUpdateRewindJobMutation()

    useErrorModal(getRewindQueueEntriesState, 'Ошибка получения очереди перемотки')
    useErrorModal(createRewindQueueEntryState, 'Ошибка создания элемента очереди перемотки')
    useErrorModal(updateRewindJobState, 'Ошибка обновления задания перемотки')
    useSuccessModal(createRewindQueueEntryState, 'Задание добавлено в очередь перемотки')

    const onCheckboxChange = (e: CheckboxChangeEvent, machine: Machine) => {
        setSelected(prev =>
            e.target.checked
                ? [...prev, machine]
                : prev.filter(item => item.documentId !== machine.documentId)
        )
    }

    const addToMachineQueue = async (machine: Machine) => {
        const currentEntries = await getRewindQueueEntries({
            filters: { machine: { documentId: { $eq: machine.documentId } } },
            fields: ['sort'],
        })

        if (!currentEntries.data) return

        const maxSort =
            currentEntries.data.data.length > 0
                ? Math.max(...currentEntries.data.data.map(item => item.sort))
                : 0
        const created = await createRewindQueueEntry({
            data: {
                zone: Zones.Main,
                rewind_job: connect(rewindJob),
                machine: connect(machine),
                sort: maxSort + 1,
            },
        })

        if (!('data' in created)) return

        const updated = await updateRewindJob({
            docId: rewindJob.documentId,
            body: {
                data: {
                    jobStatus: RewindJobStatuses.Planned,
                    assigned_by: connect(userStorage.get()),
                    assignedAt: dayjs().toISOString(),
                },
            },
        })

        if ('data' in updated) {
            dispatch(rewindJobsApi.util.invalidateTags(['RewindJobs']))
        }
    }

    const onConfirm = async () => {
        await Promise.all(selected.map(addToMachineQueue))
        setSelected([])
    }

    return (
        <Dropdown
            trigger={['hover']}
            dropdownRender={() => (
                <DropdownContent>
                    {machines.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    {[...machines]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(machine => (
                            <MachineCheckbox key={machine.documentId}>
                                <Checkbox
                                    checked={selected.some(item => item.documentId === machine.documentId)}
                                    onChange={event => onCheckboxChange(event, machine)}
                                >
                                    {machine.name}
                                </Checkbox>
                            </MachineCheckbox>
                        ))}
                    <Button
                        type='primary'
                        icon={<ArrowRightOutlined />}
                        iconPosition='end'
                        loading={
                            getRewindQueueEntriesState.isLoading ||
                            createRewindQueueEntryState.isLoading ||
                            updateRewindJobState.isLoading
                        }
                        disabled={selected.length === 0}
                        onClick={onConfirm}
                    >
                        Разместить
                    </Button>
                </DropdownContent>
            )}
        >
            <Button size='small' icon={<ArrowRightOutlined />} type='primary' />
        </Dropdown>
    )
}

export default AddToMachineQueue